import React, {useMemo} from 'react';
import {classes, style} from './doppe-action-type-services-list-widget.st.css';
import {listViewCreateDataSource, useObservableValue} from '@wix/devzai-utils-react';
import {
    convertDoppeWixServiceToServicesSettings,
    DoppeActionTypeServicesList
} from '../doppe-action-type-services-list';
import {
    DoppeActionType,
    useAssertWidgetRenderingContext
} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeWidgetItemList
} from '../../doppe-actions-common/doppe-action-type-widget-item-list/doppe-action-type-widget-item-list';
import {
    DoppeActionTypeListViewItem
} from '../../doppe-actions-common/doppe-action-type-list-view-item/doppe-action-type-list-view-item';
import {
    doppeHideableValueGetValue,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsHidden,
    doppeHideableValueIsVisibleAndNotEqualValue,
    doppeHideableValueToggleHidden
} from '../../../client-server-common/types/doppe-hideable-value';
import {
    DoppeActionTypeServiceBindableWidget
} from '../../doppe-action-type-service/doppe-action-type-service-widget/doppe-action-type-service-widget';
import {
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsAvailable,
    doppeDtoBookingServiceConvertToFormattedPrice
} from '../../../client-server-common';
import {bymoPageContextPropsResolveBusinessEntity, useBymoPageContextProps} from '../../../doppe-sdk';
import {arraySkipTake, evaluateFunction, iterableMapToArray, ObservableValue} from '@wix/devzai-utils-common';
import {
    doppeExternalServiceProviderResolveBusinessEntityReference
} from '../../../client-server-common/types/doppe-external-services';
import {
    DoppeServicesExternalDataSourceType
} from '../../../client-server-common/types/doppe-services-external-data-source';
import {DoppeAutoServiceType} from '../../../client-server-common/types/doppe-auto-service-type';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    doppeViewerActionHandleListItemClick
} from '../../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils';

export const DoppeActionTypeServicesListWidget = React.memo(function DoppeActionTypeServicesListWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeServicesList.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const {
        observableCurrentItemIdToRender
    } = useAssertWidgetRenderingContext();

    const actualObservableCurrentItemIdToRender = evaluateFunction(() => {
        return observableCurrentItemIdToRender ? observableCurrentItemIdToRender : new ObservableValue<string | null>(null)
    })

    const actionSettings = actionContext.action.settings;

    const {
        services,
        layout,
        showButton,

        externalDataSource,
        externalDataSourceItemsCount,

        previewShowCoupon,
        previewShowPrice,
        previewShowDescription,
        previewShowTitle,
        previewServiceButtonText,

        itemShowDescription,
        itemShowImage,
        itemShowTitle,
        itemShowPrice,
    } = actionSettings;

    const bymoPageContextProps = useBymoPageContextProps();

    const displayedServices = useMemo(() => {
        if (externalDataSource === null) {
            return services;
        } else {

            const externalServices = evaluateFunction(() => {
                switch (externalDataSource.type) {
                    case DoppeServicesExternalDataSourceType.LatestWixServices: {
                        return bymoPageContextPropsResolveBusinessEntity(bymoPageContextProps, {
                            businessEntity: DoppeBusinessEntity.WixAutoServicesList,
                            id: DoppeAutoServiceType.Latest
                        }) ?? []
                    }
                }
            })

            return arraySkipTake(externalServices, 0, externalDataSourceItemsCount).map(doppeDtoService => {
                return {
                    ...convertDoppeWixServiceToServicesSettings(doppeDtoService),
                    // We override the provider and the id in the provider since the values that are fetched
                    // here are already synced with the external provider.
                    serviceProvider: null,
                    serviceProviderId: null,
                } satisfies DoppeActionTypeServicesList.ServiceSettings
            });
        }
    }, [externalDataSource, externalDataSourceItemsCount, services, bymoPageContextProps])

    const expandedServiceId = useObservableValue(actualObservableCurrentItemIdToRender);

    const currentServiceToShow = useMemo(
        () => displayedServices.find(service => service.id === expandedServiceId) ?? null,
        [displayedServices, expandedServiceId]);

    const existingServices = useMemo(() => {
        return iterableMapToArray(displayedServices, (service, skip) => {

            const {
                serviceProviderId,
                serviceProvider
            } = service;

            if (serviceProvider !== null && serviceProviderId !== null) {
                const boundService = bymoPageContextPropsResolveBusinessEntity(
                    bymoPageContextProps,
                    doppeExternalServiceProviderResolveBusinessEntityReference(serviceProvider, serviceProviderId)
                );

                if (doppeBusinessEntityDtoIsAvailable(boundService)) {
                    return {
                        ...service,
                        businessEntityDto: boundService
                    }
                } else {
                    return skip;
                }
            } else {
                return {
                    ...service,
                    businessEntityDto: undefined
                }
            }
        })
    }, [displayedServices, bymoPageContextProps]);

    const servicesListViewDataSource = useMemo(() => {
        return listViewCreateDataSource(existingServices, {
            idFunction: item => item.id
        });
    }, [existingServices]);

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            {evaluateFunction(() => {
                if (!currentServiceToShow) {
                    if (servicesListViewDataSource.length === 0) {
                        return (
                            <DoppeActionUnavailablePreview
                                reason={DoppeActionUnavailableReason.ContentUnavailable}
                            />
                        )
                    } else {
                        return <DoppeActionTypeWidgetItemList
                            className={style(classes.listContent)}
                            actionContext={actionContext}
                            layout={layout}
                            hasSeeMoreButton={showButton}
                            listTitle={actionSettings.mainTitle}
                            media={doppeHideableValueGetVisibleValue(actionSettings.mainImage, undefined)}
                            onItemClick={eventData => {
                                doppeViewerActionHandleListItemClick(actionContext, {
                                    itemId: eventData.id,
                                    itemIndex: displayedServices.findIndex(item => item.id === eventData.id),
                                    itemLink: eventData.servicePageLink,
                                    couponCode: eventData.couponCode,
                                })
                            }}
                            renderItem={(dataItem, layout, index) => {
                                const {
                                    serviceImages,
                                    serviceTitle,
                                    description,
                                    priceData,
                                    businessEntityDto,
                                    ribbon,
                                } = dataItem;

                                const isHidden = doppeHideableValueIsHidden(priceData);
                                const priceDataValue = doppeHideableValueGetValue(priceData);

                                const evaluatedPriceData = businessEntityDto?.payment ?
                                    {
                                        actualPrice: doppeDtoBookingServiceConvertToFormattedPrice(businessEntityDto.payment, '')
                                    } :
                                    priceDataValue;

                                return (
                                    <DoppeActionTypeListViewItem
                                        actionContext={actionContext}
                                        innerItemId={dataItem.id}
                                        innerItemIndex={index}
                                        title={serviceTitle}
                                        description={doppeHideableValueIsVisibleAndNotEqualValue(description, '') ? description : ''}
                                        image={serviceImages?.[0] ?? null}
                                        layout={layout}
                                        price={isHidden || !itemShowPrice ? '' : evaluatedPriceData.actualPrice}
                                        isOneItem={servicesListViewDataSource.length === 1}
                                        ribbon={doppeHideableValueIsVisibleAndNotEqualValue(ribbon, '') ? doppeHideableValueGetValue(ribbon) : undefined}
                                        hideImageWhenPossible={!itemShowImage}
                                        hideTitleWhenPossible={!itemShowTitle}
                                        hideDescriptionWhenPossible={!itemShowDescription}
                                    />
                                )
                            }}
                            dataSourceItems={servicesListViewDataSource}
                        />;
                    }
                }
                else {
                    return (
                        <DoppeActionTypeServiceBindableWidget
                            actionContext={actionContext}
                            actionSettings={{
                                serviceImages: currentServiceToShow.serviceImages,
                                couponCode: doppeHideableValueToggleHidden(
                                    currentServiceToShow.couponCode,
                                    isHidden => isHidden ? true : !previewShowCoupon
                                ),
                                description: doppeHideableValueToggleHidden(
                                    currentServiceToShow.description,
                                    isHidden => isHidden ? true : !previewShowDescription
                                ),
                                serviceTitle: doppeHideableValueToggleHidden(
                                    currentServiceToShow.serviceTitle,
                                    isHidden => isHidden ? true : !previewShowTitle
                                ),
                                serviceButtonText: doppeHideableValueGetVisibleValue(
                                    currentServiceToShow.serviceButtonText,
                                    previewServiceButtonText
                                ),
                                priceData: doppeHideableValueToggleHidden(
                                    currentServiceToShow.priceData,
                                    isHidden => isHidden ? true : !previewShowPrice
                                ),
                                servicePageLink: currentServiceToShow.servicePageLink,
                                servicePeriod: currentServiceToShow.servicePeriod,
                                location: currentServiceToShow.location,
                                serviceSchedule: currentServiceToShow.serviceSchedule,
                                serviceParticipants: currentServiceToShow.serviceParticipants,
                                ribbon: currentServiceToShow.ribbon,
                                serviceProvider: currentServiceToShow.serviceProvider ?? null,
                                serviceProviderId: currentServiceToShow.serviceProviderId ?? null,
                            }}
                            reportGoalAchieved={(goalType) => {
                                actionContext.reportGoalAchieved({
                                    goalType: goalType,
                                    innerItemId: currentServiceToShow?.id,
                                    innerItemPosition: existingServices.findIndex(item => item.id === currentServiceToShow?.id),
                                });
                            }}
                            actionId={actionContext.action.id}
                        />
                    )
                }
            })}
        </DoppeActionTypeWidgetLayout>
    );
});


